import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetPracticeAreasWithFiltersNotPaginatedQuery } from 'store/api/practice-areas';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { PracticeArea } from 'types/practice-areas';
import { Team, TeamForm, TeamType } from 'types/teams';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

export const TEAM_FORM_CLASSIFIERS = ['TEAM_TYPE', 'BUSINESS_AREA_TYPE'];

/**
 * Who has the team form's DMS group field: a user with either right, behind the flag. The read view asks too, to
 * know whether the form shows the field.
 */
export const useDmsTeamFieldGate = () => {
    const hasBackOfficeSettingsPermission = useHasPermissions([
        'READ.BACK_OFFICE_SETTINGS',
        'GET.BACK_OFFICE_SETTINGS_WO_READ',
    ]);
    const isDmsTeamAdIntegrationEnabled = useIsFeatureEnabled(FeatureFlag.DMS_TEAM_AD_INTEGRATION_ENABLED);
    return {
        hasBackOfficeSettingsPermission,
        isDmsTeamAdIntegrationEnabled,
        hasDmsTeamField: hasBackOfficeSettingsPermission && isDmsTeamAdIntegrationEnabled,
    };
};

const defaultValues: TeamForm = {
    name: '',
    abbreviation: '',
    teamType: null,
    isActive: true,
    parentTeam: null,
    teamPracticeAreas: [],
    emailAddress: '',
    readonlyTeamEmployees: [],
    teamOffices: [],
    isParent: false,
    teamBusinessAreas: [],
    dmsTeam: null,
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

interface Props {
    initialData?: TeamForm;
    classifiers: Record<string, ClassifierData>;
    entityCustomFields?: CustomField[];
    team?: Team;
}

/**
 * The team form's state, shared by the create and edit pages and the cards: the type, parent and practice group
 * fields drive one another, and a correspondence team is always a parent.
 */
export const useTeamForm = ({ initialData, classifiers, entityCustomFields = EMPTY_CUSTOM_FIELDS, team }: Props) => {
    const { hasBackOfficeSettingsPermission, hasDmsTeamField } = useDmsTeamFieldGate();

    const methods = useForm<TeamForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
    });

    const { control, setValue, formState } = methods;

    const [teamType, isParent, parentTeam] = useWatch({ control, name: ['teamType', 'isParent', 'parentTeam'] });
    const isPracticeGroup = teamType?.value === 'PRACTICE_GROUP' && isParent;
    // Validate if email field matches email pattern
    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);

    const { data: practiceAreaList, isLoading: isLoadingPracticeAreaList } =
        useGetPracticeAreasWithFiltersNotPaginatedQuery({
            filters: 'practiceAreaTypeCode==PRACTICE_AREA;id=ispracticeareaavailableforpracticegroup=true',
        });
    const { currentData: parentPracticeGroupPracticeAreas, isFetching: isFetchingParentPracticeGroupPracticeAreas } =
        useGetPracticeAreasWithFiltersNotPaginatedQuery(
            {
                filters: `practiceAreaTypeCode==PRACTICE_AREA;teamPracticeAreas.team.id==${parentTeam?.id}`,
            },
            { skip: parentTeam === null }
        );
    const isLoadingNewParentTeamData =
        isFetchingParentPracticeGroupPracticeAreas && parentPracticeGroupPracticeAreas === undefined;

    // A child team shows its parent group's practice areas.
    const parentPracticeAreaList: PracticeArea[] | undefined =
        !isParent && parentTeam != null ? parentPracticeGroupPracticeAreas : [];

    useEffect(() => {
        if (teamType?.value !== TeamType.CORRESPONDENCE) {
            if (!formState.dirtyFields.isParent && isParent !== initialData?.isParent) {
                setValue('isParent', initialData?.isParent || defaultValues.isParent, { shouldDirty: false });
            }
            return;
        }
        if (!formState.dirtyFields.isParent && !isParent) {
            setValue('isParent', true, { shouldDirty: false });
        }
    }, [teamType, isParent, formState.dirtyFields, setValue, initialData]);

    const businessAreaOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.TEAM,
        fields: entityCustomFields,
    });

    return {
        methods,
        classifiers,
        team,
        hasBackOfficeSettingsPermission,
        hasDmsTeamField,
        teamType,
        isParent,
        isPracticeGroup,
        parentPracticeAreaList,
        isLoadingNewParentTeamData,
        practiceAreaList,
        businessAreaOptions,
        validEmail,
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData || isLoadingPracticeAreaList,
    };
};

export type TeamFormState = ReturnType<typeof useTeamForm>;
